import { Component, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { WebSocketService, WebSocketBookingAllocation } from '../../services/websocket.service';

/**
 * In-app notification banner for new booking allocations received via WebSocket.
 * Displays client name, service name, and start time.
 * Auto-dismisses after 5 seconds or can be manually dismissed.
 *
 * Validates: Requirements 14.3
 */
@Component({
    selector: 'app-notification-banner',
    imports: [CommonModule, TranslateModule],
    template: `
    @if (visible()) {
      <div
        class="notification-banner"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div class="notification-content">
          <span class="notification-title">{{ 'notifications.new_booking' | translate }}</span>
          <span class="notification-details">
            {{ 'notifications.booking_details' | translate:{ client: clientName(), service: serviceName(), time: startTime() } }}
          </span>
        </div>
        <button
          class="notification-dismiss"
          type="button"
          (click)="dismiss()"
          [attr.aria-label]="'common.close' | translate"
        >
          &times;
        </button>
      </div>
    }
  `,
    styles: [`
    .notification-banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      background-color: #e3f2fd;
      border-bottom: 1px solid #90caf9;
      color: #1565c0;
      font-size: 14px;
      animation: slideDown 0.3s ease-out;
    }

    @keyframes slideDown {
      from {
        transform: translateY(-100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    .notification-content {
      display: flex;
      flex-direction: column;
      gap: 2px;
      flex: 1;
      min-width: 0;
    }

    .notification-title {
      font-weight: 600;
      font-size: 13px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .notification-details {
      font-size: 14px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .notification-dismiss {
      background: none;
      border: none;
      font-size: 20px;
      line-height: 1;
      cursor: pointer;
      color: #1565c0;
      padding: 4px 8px;
      margin-left: 12px;
      border-radius: 4px;
      flex-shrink: 0;
    }

    .notification-dismiss:hover {
      background-color: rgba(21, 101, 192, 0.1);
    }

    .notification-dismiss:focus-visible {
      outline: 2px solid #1565c0;
      outline-offset: 2px;
    }
  `]
})
export class NotificationBannerComponent implements OnInit, OnDestroy {
  private readonly webSocketService = inject(WebSocketService);

  /** Whether the banner is currently visible. */
  readonly visible = signal(false);

  /** Client name from the latest booking allocation. */
  readonly clientName = signal('');

  /** Service name from the latest booking allocation. */
  readonly serviceName = signal('');

  /** Formatted start time from the latest booking allocation. */
  readonly startTime = signal('');

  private subscription: Subscription | null = null;
  private dismissTimer: ReturnType<typeof setTimeout> | null = null;

  /** Auto-dismiss timeout in milliseconds. */
  static readonly AUTO_DISMISS_MS = 5000;

  ngOnInit(): void {
    this.subscription = this.webSocketService.bookingAllocations$.subscribe(
      (message) => this.showNotification(message)
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.clearDismissTimer();
  }

  /** Manually dismiss the notification banner. */
  dismiss(): void {
    this.visible.set(false);
    this.clearDismissTimer();
  }

  /** Show the notification banner with booking details. */
  private showNotification(message: WebSocketBookingAllocation): void {
    this.clientName.set(message.booking.client_name);
    this.serviceName.set(message.booking.service_name);
    this.startTime.set(this.formatTime(message.booking.start_dt));
    this.visible.set(true);

    // Reset the auto-dismiss timer
    this.clearDismissTimer();
    this.dismissTimer = setTimeout(() => {
      this.visible.set(false);
    }, NotificationBannerComponent.AUTO_DISMISS_MS);
  }

  /** Format an ISO date string to a locale-friendly time. */
  private formatTime(isoDate: string): string {
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) {
        return isoDate;
      }
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return isoDate;
    }
  }

  private clearDismissTimer(): void {
    if (this.dismissTimer !== null) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  }
}
